import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Anime,
  AnimeSeason,
  AnimeSource,
  BroadcastDay,
  BROADCAST_DAYS,
  LineNotifyConfig,
  TierGrade,
  WakuFeedItem,
} from './types/anime';
import {
  exportAnimeBackupJSON,
  getLineConfig,
  getLocalAnimeList,
  saveLineConfig,
  saveLocalAnimeList,
  startCloudSync,
  repairKnownBadLinks,
} from './services/storage';
import {
  checkSingleAnimeUpdate,
  audioTypeFromSources,
  findCrossPlatformLinks,
  sendLineNotification,
  saveServerAlertConfig,
  getAllPlatformFeeds,
  LINKED_PLATFORM_TYPES,
  fetchRukaScheduleEntries,
} from './services/animeService';
import { applyRukaSchedule, RukaScheduleEntry } from './utils/rukaSchedule';
import { AiringFilter, matchesAiringFilter, stampFinishedDates } from './utils/airingStatus';
import {
  airtimeBadge,
  dayKey,
  dueAirtimeAlerts,
  newEpisodeAlerts,
  newEpisodeBadge,
} from './utils/lineAlerts';
import {
  getTodayBroadcastDay,
  isAiringToday,
  isOnBreak,
  areAnimeMatching,
  mergeDuplicateAnime,
  resolveAnimeTotalEpisodes,
  enrichAnimeSourcesAndSchedule,
  isDuplicateSource,
  isGenericThaiTitle,
  hasConflictingSource,
  withSourceAudio,
} from './utils/helpers';
import { ActiveViewMode, Header } from './components/Header';
import { SeasonHeroBar, FilterValues } from './components/SeasonHeroBar';
import { TierBoard } from './components/TierBoard';
import { ScheduleBoard } from './components/ScheduleBoard';
import { WakuExplorerView } from './components/WakuExplorerView';
import { AnimeModal } from './components/AnimeModal';
import { AnimeDetailModal } from './components/AnimeDetailModal';
import { OfflineLibraryModal } from './components/OfflineLibraryModal';
import { LineNotifyModal } from './components/LineNotifyModal';
import { CheckCircle2, AlertCircle, Loader2, Sparkles } from 'lucide-react';

export const App: React.FC = () => {
  const [animeList, setAnimeList] = useState<Anime[]>(() => getLocalAnimeList());
  const animeListRef = useRef(animeList);
  animeListRef.current = animeList;
  const [lineConfig, setLineConfig] = useState<LineNotifyConfig>(() =>
    getLineConfig()
  );

  // Navigation & Filters
  const [activeView, setActiveView] = useState<ActiveViewMode>('tier');
  const [selectedSeason, setSelectedSeason] = useState<AnimeSeason | 'ALL'>('ALL');
  // Home opens on this year's shows that are airing or just ended
  const [selectedYear, setSelectedYear] = useState<number | 'ALL'>(() => new Date().getFullYear());
  const [airingFilter, setAiringFilter] = useState<AiringFilter>('AIRING_RECENT');
  const [selectedAudio, setSelectedAudio] = useState<
    'ALL' | 'ซับไทย' | 'พากย์ไทย'
  >('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [isSortMode, setIsSortMode] = useState(false);

  // Modals
  const [isAnimeModalOpen, setIsAnimeModalOpen] = useState(false);
  const [editingAnime, setEditingAnime] = useState<Anime | null>(null);
  const [detailAnime, setDetailAnime] = useState<Anime | null>(null);
  const [isLineModalOpen, setIsLineModalOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  // Episode Checker & LINE Sender States
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [checkProgress, setCheckProgress] = useState<{
    current: number;
    total: number;
    title: string;
    updatedCount: number;
  } | null>(null);
  const [isSendingLine, setIsSendingLine] = useState(false);

  const [toast, setToast] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  const showToast = useCallback(
    (text: string, type: 'success' | 'error' | 'info' = 'success') => {
      setToast({ text, type });
      setTimeout(() => {
        setToast(null);
      }, 4500);
    },
    []
  );

  const updateAnimeState = useCallback((nextList: Anime[]) => {
    setAnimeList(nextList);
    saveLocalAnimeList(nextList);
  }, []);

  // Search Bilibili / Anime-Waku / Ani-One catalogues for platforms an anime has no link on yet
  const fillMissingLinks = useCallback(async (targets: Anime[], announce = false) => {
    const found = new Map<string, AnimeSource[]>();
    for (const anime of targets) {
      found.set(anime.id, await findCrossPlatformLinks(anime).catch(() => []));
    }
    let added: AnimeSource[] = [];
    setAnimeList((prev) => {
      const next = prev.map((a) => {
        if (!found.has(a.id)) return a;
        const fresh = (found.get(a.id) || []).filter(
          (f) => !a.sources.some((s) => isDuplicateSource(s, f))
        );
        if (fresh.length) added = fresh;
        const sources = [...a.sources, ...fresh];
        return {
          ...a,
          sources,
          audioType: audioTypeFromSources(sources, a.audioType),
          linksCheckedAt: Date.now(),
        };
      });
      saveLocalAnimeList(next);
      return next;
    });
    if (announce && added.length) {
      showToast(`เพิ่มลิงก์ ${added.map((s) => s.name).join(', ')} ให้อัตโนมัติ`, 'success');
    }
  }, []);

  // Every few days, look for missing platform links on saved anime (in the background)
  useEffect(() => {
    const LINK_RECHECK_MS = 3 * 24 * 60 * 60 * 1000;
    // Bump when a platform is added to the link search, so every saved show is checked once more
    const FULL_RECHECK_KEY = 'anime_ranker_links_recheck_v4_anione_feed_spelling';
    let fullRecheck = false;
    try {
      fullRecheck = localStorage.getItem(FULL_RECHECK_KEY) !== 'done';
    } catch {}
    const timer = setTimeout(async () => {
      const due = animeListRef.current
        .filter(
          (a) =>
            LINKED_PLATFORM_TYPES.some((t) => !a.sources.some((s) => s.type === t)) &&
            (fullRecheck || !a.linksCheckedAt || Date.now() - a.linksCheckedAt > LINK_RECHECK_MS)
        )
        .slice(0, 60);
      if (due.length) await fillMissingLinks(due);
      if (fullRecheck) {
        try {
          localStorage.setItem(FULL_RECHECK_KEY, 'done');
        } catch {}
      }
    }, 12000);
    return () => clearTimeout(timer);
  }, [fillMissingLinks]);

  // One-time cleanup: drop sources that link to a different show (and the episodes they brought in)
  useEffect(() => {
    const AUDIT_KEY = 'anime_ranker_source_audit_v1';
    try {
      if (localStorage.getItem(AUDIT_KEY) === 'done') return;
    } catch {
      return;
    }
    const timer = setTimeout(async () => {
      const snapshot = animeListRef.current;
      const repaired = new Map<string, Anime>();
      for (const anime of snapshot) {
        try {
          const res = await checkSingleAnimeUpdate(anime);
          if (res.updatedAnime.sources.length < anime.sources.length) {
            repaired.set(anime.id, res.updatedAnime);
          }
        } catch {}
      }
      if (repaired.size > 0) {
        setAnimeList((prev) => {
          const next = prev.map((a) => repaired.get(a.id) || a);
          saveLocalAnimeList(next);
          return next;
        });
      }
      try {
        localStorage.setItem(AUDIT_KEY, 'done');
      } catch {}
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-enrich existing anime list on startup with multi-platform sources & correct schedule
  useEffect(() => {
    let isMounted = true;

    // The cloud copy (Cloudflare KV) replaces this device's copy whenever it's newer
    const stopCloudSync = startCloudSync((rawRemote) => {
      if (!isMounted) return;
      const repaired = repairKnownBadLinks(rawRemote);
      if (repaired.changed) saveLocalAnimeList(repaired.list);
      setAnimeList(repaired.list);
    });

    getAllPlatformFeeds().then((feeds) => {
      if (!isMounted || !feeds.all.length) return;
      setAnimeList((prev) => {
        let changed = false;
        const next = prev.map((anime) => {
          const enriched = enrichAnimeSourcesAndSchedule(anime, feeds.all);
          const hasMoreSources = enriched.sources.length > anime.sources.length;
          const hasBetterSchedule =
            anime.broadcastDay === 'UNKNOWN' && enriched.broadcastDay !== 'UNKNOWN';
          const hasBetterThai =
            Boolean(
              anime.thaiTitle &&
              anime.thaiTitle.startsWith('ตอนใหม่ทุกวัน') &&
              enriched.bestThaiTitle &&
              !enriched.bestThaiTitle.startsWith('ตอนใหม่ทุกวัน')
            );
          const hasBetterTotal = enriched.totalEp !== anime.totalEp && enriched.totalEp > 0;

          if (hasMoreSources || hasBetterSchedule || hasBetterThai || hasBetterTotal) {
            changed = true;
            return {
              ...anime,
              title: anime.title || enriched.bestTitle,
              thaiTitle: enriched.bestThaiTitle || anime.thaiTitle,
              imageUrl: enriched.bestImageUrl || anime.imageUrl,
              broadcastDay:
                !anime.scheduleSource && enriched.broadcastDay !== 'UNKNOWN'
                  ? enriched.broadcastDay
                  : anime.broadcastDay,
              broadcastTime:
                anime.scheduleSource
                  ? anime.broadcastTime
                  : enriched.broadcastTime || anime.broadcastTime,
              latestEp: Math.max(anime.latestEp, enriched.latestEp),
              totalEp: enriched.totalEp || anime.totalEp,
              sources: enriched.sources,
              detectedDayNote: enriched.detectedDayNote || anime.detectedDayNote,
            };
          }
          return anime;
        });

        if (changed) {
          saveLocalAnimeList(next);
          return next;
        }
        return prev;
      });
    });

    return () => {
      isMounted = false;
      stopCloudSync();
    };
  }, []);

  // AnimeRuka's weekly schedule drives every card's broadcast day/time. Re-read it every few
  // minutes and whenever the app comes back to the foreground, so a moved slot shows up quickly.
  const [rukaSchedule, setRukaSchedule] = useState<RukaScheduleEntry[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      fetchRukaScheduleEntries().then((entries) => {
        if (alive && entries.length > 0) setRukaSchedule(entries);
      });
    };
    load();
    const timer = setInterval(load, 3 * 60 * 1000);
    document.addEventListener('visibilitychange', load);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', load);
    };
  }, []);

  // Re-applied whenever the list changes too (cloud sync, episode checks, new shows), so nothing
  // else can leave a card on an older time
  useEffect(() => {
    if (rukaSchedule.length === 0) return;
    setAnimeList((prev) => {
      const { list, changed } = applyRukaSchedule(prev, rukaSchedule);
      if (!changed) return prev;
      saveLocalAnimeList(list);
      return list;
    });
  }, [animeList, rukaSchedule]);

  // LINE alerts. Sent from this device only when it has LINE / a webhook set up; each show
  // remembers what it was alerted for, so other devices and later runs don't repeat it.
  const lineConfigRef = useRef(lineConfig);
  lineConfigRef.current = lineConfig;
  // Once the server has the settings it sends every alert itself; the browser stays quiet so
  // nothing arrives twice
  const lineReady =
    Boolean(lineConfig.channelAccessToken || lineConfig.webhookUrl) && !lineConfig.serverAlerts;

  const applyLineConfig = useCallback(
    (cfg: LineNotifyConfig, announce: boolean) => {
      setLineConfig(cfg);
      saveLineConfig(cfg);
      saveServerAlertConfig(cfg).then((serverAlerts) => {
        const next = { ...cfg, serverAlerts };
        setLineConfig(next);
        saveLineConfig(next);
        if (announce) {
          showToast(
            serverAlerts
              ? 'บันทึกแล้ว — แจ้งเตือนจะส่งจากเซิร์ฟเวอร์ทุก 20 นาที แม้ปิดเว็บ'
              : 'บันทึกการตั้งค่าแจ้งเตือนแล้ว (ยังส่งจากเซิร์ฟเวอร์ไม่ได้ จะส่งตอนเปิดเว็บแทน)',
            serverAlerts ? 'success' : 'info'
          );
        }
      });
    },
    [showToast]
  );

  // Settings saved before background alerts existed: hand them to the server once
  useEffect(() => {
    if ((lineConfig.channelAccessToken || lineConfig.webhookUrl) && !lineConfig.serverAlerts) {
      applyLineConfig(lineConfig, false);
    }
  }, []);
  const alertsInFlight = useRef(new Set<string>());

  const markAnime = useCallback((id: string, patch: (a: Anime) => Partial<Anime>) => {
    setAnimeList((prev) => {
      const next = prev.map((a) => (a.id === id ? { ...a, ...patch(a) } : a));
      saveLocalAnimeList(next);
      return next;
    });
  }, []);

  // New episode on a show with its bell on — whichever part of the app found it
  useEffect(() => {
    const { baselines, alerts } = newEpisodeAlerts(animeList);
    if (baselines.size > 0) {
      setAnimeList((prev) => {
        const next = prev.map((a) =>
          baselines.has(a.id) && a.lastNotifiedEp === undefined
            ? { ...a, lastNotifiedEp: baselines.get(a.id)! }
            : a
        );
        saveLocalAnimeList(next);
        return next;
      });
    }
    if (!lineReady || !lineConfig.notifyOnNewEpFound) return;
    for (const alert of alerts) {
      const key = `${alert.anime.id}:ep${alert.toEp}`;
      if (alertsInFlight.current.has(key)) continue;
      alertsInFlight.current.add(key);
      sendLineNotification(lineConfigRef.current, {
        anime: alert.anime,
        badge: newEpisodeBadge(alert),
      }).then((res) => {
        if (res.success) {
          markAnime(alert.anime.id, (a) => ({
            lastNotifiedEp: Math.max(a.lastNotifiedEp ?? 0, alert.toEp),
          }));
        } else {
          showToast(`ส่งแจ้งเตือนตอนใหม่ไม่สำเร็จ: ${res.error}`, 'error');
        }
      });
    }
  }, [animeList, lineReady, lineConfig.notifyOnNewEpFound, markAnime, showToast]);

  // Airtime: when a bell-on show's time slot comes round (checked every minute while open)
  useEffect(() => {
    if (!lineReady || !lineConfig.notifyOnSchedule) return;
    const tick = () => {
      const now = new Date();
      const today = dayKey(now);
      for (const anime of dueAirtimeAlerts(animeListRef.current, now)) {
        const key = `${anime.id}:airtime:${today}`;
        if (alertsInFlight.current.has(key)) continue;
        alertsInFlight.current.add(key);
        sendLineNotification(lineConfigRef.current, { anime, badge: airtimeBadge(anime) }).then(
          (res) => {
            if (res.success) markAnime(anime.id, () => ({ lastAirtimeAlertOn: today }));
          }
        );
      }
    };
    tick();
    const timer = setInterval(tick, 60 * 1000);
    return () => clearInterval(timer);
  }, [lineReady, lineConfig.notifyOnSchedule, markAnime]);

  // Remember the day each show ended (for its 3-week "เพิ่งจบ" window)
  useEffect(() => {
    setAnimeList((prev) => {
      const { list, changed } = stampFinishedDates(prev);
      if (!changed) return prev;
      saveLocalAnimeList(list);
      return list;
    });
  }, [animeList]);

  // Filtered Anime List. Every filter goes through this one check, so the counts shown next to a
  // filter option ("what if I picked this") use all the other active filters too.
  const currentFilters: FilterValues = {
    season: selectedSeason,
    year: selectedYear,
    airing: airingFilter,
    audio: selectedAudio,
  };
  const matchesFilters = (anime: Anime, f: FilterValues) => {
    if (f.season !== 'ALL' && anime.season !== f.season) return false;
    if (f.year !== 'ALL' && anime.year !== f.year) return false;
    if (!matchesAiringFilter(anime, f.airing)) return false;
    if (f.audio !== 'ALL' && anime.audioType !== f.audio && anime.audioType !== 'ซับ+พากย์') {
      return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const matchEn = anime.title.toLowerCase().includes(q);
      const matchTh = (anime.thaiTitle || '').toLowerCase().includes(q);
      if (!matchEn && !matchTh) return false;
    }
    return true;
  };
  const filteredAnimeList = animeList.filter((anime) => matchesFilters(anime, currentFilters));
  const countWithFilters = (override: Partial<FilterValues>) => {
    const f = { ...currentFilters, ...override };
    return animeList.filter((anime) => matchesFilters(anime, f)).length;
  };
  const resetFilters = () => {
    setSelectedSeason('ALL');
    setSelectedYear(new Date().getFullYear());
    setAiringFilter('AIRING_RECENT');
    setSelectedAudio('ALL');
  };

  // Quick +1 Episode
  const handleQuickPlus = (id: string) => {
    setAnimeList((prev) => {
      const next = prev.map((a) => {
        if (a.id === id) {
          const nextWatched = (Number(a.watchedEp) || 0) + 1;
          const nextLatest = Math.max(Number(a.latestEp) || 0, nextWatched);
          // Watching past the known latest episode isn't a release to alert about
          const lastNotifiedEp =
            a.lastNotifiedEp === undefined ? undefined : Math.max(a.lastNotifiedEp, nextLatest);
          return { ...a, watchedEp: nextWatched, latestEp: nextLatest, lastNotifiedEp };
        }
        return a;
      });
      saveLocalAnimeList(next);
      return next;
    });
  };

  // Quick -1 Episode
  const handleQuickMinus = (id: string) => {
    setAnimeList((prev) => {
      const next = prev.map((a) => {
        if (a.id === id) {
          return { ...a, watchedEp: Math.max(0, (Number(a.watchedEp) || 0) - 1) };
        }
        return a;
      });
      saveLocalAnimeList(next);
      return next;
    });
  };

  // Toggle LINE Notification per anime
  const handleToggleNotify = (id: string) => {
    setAnimeList((prev) => {
      const next = prev.map((a) => {
        if (a.id === id) {
          const nextNotify = !a.notifyLine;
          showToast(
            `${nextNotify ? '🔔 เปิด' : '🔕 ปิด'}แจ้งเตือน LINE สำหรับ "${a.title}"`,
            'info'
          );
          return { ...a, notifyLine: nextNotify };
        }
        return a;
      });
      saveLocalAnimeList(next);
      return next;
    });
  };

  // Change Broadcast Day directly from Schedule
  const handleChangeBroadcastDay = (id: string, day: BroadcastDay) => {
    const dayName =
      BROADCAST_DAYS.find((d) => d.id === day)?.nameTh || 'ไม่ระบุวัน';
    setAnimeList((prev) => {
      const next = prev.map((a) => (a.id === id ? { ...a, broadcastDay: day } : a));
      saveLocalAnimeList(next);
      return next;
    });
    showToast(`ย้ายวันออกอากาศไปที่ "${dayName}" เรียบร้อยแล้ว!`, 'success');
  };

  // Save Add / Edit Anime
  const handleSaveAnime = (saved: Anime) => {
    setAnimeList((prev) => {
      const existing = prev.find((a) => a.id === saved.id);
      // New shows and shows moved to another tier go after that tier's last card
      const lastOrderIn = (grade: string) =>
        prev
          .filter((a) => a.grade === grade && a.id !== saved.id)
          .reduce((max, a) => Math.max(max, a.order ?? 0), 0);
      let next: Anime[];
      if (existing) {
        const moved =
          existing.grade !== saved.grade
            ? { ...saved, order: lastOrderIn(saved.grade) + 1 }
            : saved;
        // An episode count typed in by hand (or read in the detail view you're looking at) isn't news
        const updated =
          existing.lastNotifiedEp !== undefined && (Number(saved.latestEp) || 0) > existing.lastNotifiedEp
            ? { ...moved, lastNotifiedEp: Number(saved.latestEp) || 0 }
            : moved;
        next = prev.map((a) => (a.id === saved.id ? updated : a));
        showToast(`บันทึกการแก้ไข "${saved.title}" สำเร็จ!`);
      } else {
        next = [...prev, { ...saved, order: lastOrderIn(saved.grade) + 1 }];
        showToast(`เพิ่มอนิเมะ "${saved.title}" เข้าสู่ ${saved.grade} Tier สำเร็จ!`);
      }
      saveLocalAnimeList(next);
      return next;
    });
    // A newly added show: link the same show on the other platforms too
    if (!animeListRef.current.some((a) => a.id === saved.id)) {
      setTimeout(() => fillMissingLinks([saved], true), 300);
    }
  };

  const handleDeleteAnime = (id: string) => {
    setAnimeList((prev) => {
      const next = prev.filter((a) => a.id !== id);
      saveLocalAnimeList(next);
      return next;
    });
    showToast('ลบอนิเมะออกจากรายการแล้ว', 'info');
  };

  // Quick Add from Explorer with Smart Multi-platform Merge
  const handleQuickAddFromWaku = async (item: WakuFeedItem) => {
    // 1. Fetch all platform feeds (Ani-One, Bilibili, Anime-Waku, WeTV)
    const feeds = await getAllPlatformFeeds();

    // 2. Enrich item across all platforms: gather all platform sources,
    // pick earliest broadcast timetable, tie-break with Ani-One link, resolve total episodes.
    const enriched = enrichAnimeSourcesAndSchedule(item, feeds.all);

    const tempAnime: Anime = {
      id: '',
      title: enriched.bestTitle,
      thaiTitle: enriched.bestThaiTitle,
      // Picked from Explore: park it in EX until it's been ranked
      grade: 'EX',
      season: item.season,
      year: item.year,
      watchedEp: 0,
      latestEp: enriched.latestEp,
      totalEp: enriched.totalEp,
      imageUrl: enriched.bestImageUrl || item.imageUrl,
      status: item.status,
      audioType: audioTypeFromSources(enriched.sources, item.audioType),
      broadcastDay: enriched.broadcastDay,
      broadcastTime: enriched.broadcastTime,
      notifyLine: true,
      order: 0,
      sources: enriched.sources,
      detectedDayNote: enriched.detectedDayNote,
    };

    setAnimeList((prev) => {
      const matchIndex = prev.findIndex(
        (a) =>
          a.sources.some((s) => item.url && s.url === item.url) ||
          (!hasConflictingSource(a, item) && areAnimeMatching(a, tempAnime))
      );

      let next: Anime[];
      if (matchIndex >= 0) {
        const target = prev[matchIndex];
        // Combine sources
        const combinedSources = [...target.sources];
        for (const s of enriched.sources) {
          if (!combinedSources.some((cs) => isDuplicateSource(cs, s))) {
            combinedSources.push(withSourceAudio(s));
          }
        }
        // Place winning source at index 0 (Ani-One if tie)
        if (enriched.sources[0]) {
          const winType = enriched.sources[0].type;
          const winIdx = combinedSources.findIndex((s) => s.type === winType);
          if (winIdx > 0) {
            const [win] = combinedSources.splice(winIdx, 1);
            combinedSources.unshift(win);
          }
        }

        const updated: Anime = {
          ...target,
          title: target.title || enriched.bestTitle,
          thaiTitle: !isGenericThaiTitle(target.thaiTitle)
            ? target.thaiTitle
            : enriched.bestThaiTitle || '',
          sources: combinedSources,
          audioType: audioTypeFromSources(combinedSources, target.audioType),
          latestEp: Math.max(Number(target.latestEp) || 0, enriched.latestEp),
          totalEp: enriched.totalEp || target.totalEp,
          broadcastDay:
            !target.scheduleSource && enriched.broadcastDay !== 'UNKNOWN'
              ? enriched.broadcastDay
              : target.broadcastDay,
          broadcastTime:
            target.scheduleSource
              ? target.broadcastTime
              : enriched.broadcastTime || target.broadcastTime,
          imageUrl:
            target.imageUrl.includes('cdn.myanimelist.net')
              ? target.imageUrl
              : enriched.bestImageUrl || target.imageUrl,
          detectedDayNote: enriched.detectedDayNote || target.detectedDayNote,
        };
        next = prev.map((a, i) => (i === matchIndex ? updated : a));
        showToast(
          `อัปเดตช่องทางของ "${target.title}" รวม ${combinedSources.length} แพลตฟอร์มสำเร็จ!`,
          'success'
        );
      } else {
        const tierItems = prev.filter((a) => a.grade === tempAnime.grade);
        const maxOrder = tierItems.reduce(
          (max, a) => Math.max(max, a.order ?? 0),
          0
        );
        const newAnime: Anime = {
          ...tempAnime,
          id: `ANIME_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          order: maxOrder + 1,
        };
        next = [...prev, newAnime];
        showToast(
          `เพิ่ม "${newAnime.title}" เข้า EX Tier แล้ว (${enriched.sources.length} แพลตฟอร์ม)`,
          'success'
        );
      }
      saveLocalAnimeList(next);
      return next;
    });

    // The airing feeds miss finished seasons on other platforms — search their whole catalogues
    setTimeout(() => {
      const saved = animeListRef.current.find((a) => a.sources.some((s) => s.url === item.url));
      if (saved) fillMissingLinks([saved], true);
    }, 300);
  };

  // Quick Remove from Explorer (Toggle button: un-add)
  const handleQuickRemoveFromWaku = (item: WakuFeedItem) => {
    const tempAnime: Anime = {
      id: '',
      title: item.romajiTitle || item.displayTitle,
      thaiTitle: item.thaiTitle || '',
      grade: 'A',
      season: item.season,
      year: item.year,
      watchedEp: 0,
      latestEp: item.latestEp || 1,
      totalEp: 12,
      imageUrl: '',
      status: item.status,
      audioType: item.audioType,
      broadcastDay: 'UNKNOWN',
      notifyLine: true,
      order: 0,
      sources: [{ type: 'other', name: '', url: item.url, latestEp: 1 }],
    };

    setAnimeList((prev) => {
      const matchIndex = prev.findIndex(
        (a) =>
          a.sources.some((s) => s.url === item.url) ||
          (!hasConflictingSource(a, item) && areAnimeMatching(a, tempAnime))
      );
      if (matchIndex < 0) return prev;
      const target = prev[matchIndex];
      const next = prev.filter((_, idx) => idx !== matchIndex);
      saveLocalAnimeList(next);
      showToast(`นำ "${target.title}" ออกจากลิสต์แล้ว`, 'info');
      return next;
    });
  };

  // Merge Duplicate Anime
  const handleMergeDuplicates = useCallback(() => {
    setAnimeList((prev) => {
      const { mergedList, mergedCount } = mergeDuplicateAnime(prev);
      if (mergedCount === 0) {
        showToast('ไม่พบรายการที่ซ้ำกัน รายการทั้งหมดรวมเรียบร้อยแล้ว ✨', 'info');
        return prev;
      }
      saveLocalAnimeList(mergedList);
      showToast(`รวมเรื่องที่ซ้ำกันเรียบร้อยแล้ว (${mergedCount} เรื่อง) 🎉`, 'success');
      return mergedList;
    });
  }, []);

  // Drag & Drop Reordering inside / across Tiers
  const handleReorder = (draggedId: string, targetId: string) => {
    setAnimeList((prev) => {
      const dragged = prev.find((a) => a.id === draggedId);
      const target = prev.find((a) => a.id === targetId);
      if (!dragged || !target) return prev;

      const targetGrade = target.grade;
      const tierItems = prev
        .filter((a) => a.grade === targetGrade && a.id !== draggedId)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const targetIdx = tierItems.findIndex((a) => a.id === targetId);
      const updatedDragged = { ...dragged, grade: targetGrade };
      tierItems.splice(targetIdx >= 0 ? targetIdx : tierItems.length, 0, updatedDragged);

      const reorderedTier = tierItems.map((item, idx) => ({
        ...item,
        order: idx + 1,
      }));

      const others = prev.filter(
        (a) => a.grade !== targetGrade && a.id !== draggedId
      );
      const next = [...others, ...reorderedTier];
      saveLocalAnimeList(next);
      return next;
    });
  };

  const handleMoveToTier = (animeId: string, targetGrade: TierGrade) => {
    setAnimeList((prev) => {
      const targetItems = prev.filter((a) => a.grade === targetGrade);
      const maxOrder = targetItems.reduce(
        (max, a) => Math.max(max, a.order ?? 0),
        0
      );
      const next = prev.map((a) =>
        a.id === animeId ? { ...a, grade: targetGrade, order: maxOrder + 1 } : a
      );
      saveLocalAnimeList(next);
      return next;
    });
  };

  const handleMoveUpDown = (id: string, dir: 'up' | 'down') => {
    setAnimeList((prev) => {
      const target = prev.find((a) => a.id === id);
      if (!target) return prev;
      const tierItems = prev
        .filter((a) => a.grade === target.grade)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = tierItems.findIndex((a) => a.id === id);
      const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= tierItems.length) return prev;

      const temp = tierItems[idx];
      tierItems[idx] = tierItems[swapIdx];
      tierItems[swapIdx] = temp;

      const updatedTier = tierItems.map((item, i) => ({ ...item, order: i + 1 }));
      const others = prev.filter((a) => a.grade !== target.grade);
      const next = [...others, ...updatedTier];
      saveLocalAnimeList(next);
      return next;
    });
  };

  // Check Updates across all Anime on Anime-Waku & YouTube
  const handleCheckUpdates = useCallback(async () => {
    if (isCheckingUpdates) return;
    setIsCheckingUpdates(true);

    const targets = animeList.filter((a) => a.status !== 'จบแล้ว');
    let updatedCount = 0;
    const updatedList = [...animeList];

    for (let i = 0; i < targets.length; i++) {
      const item = targets[i];
      setCheckProgress({
        current: i + 1,
        total: targets.length,
        title: item.title,
        updatedCount,
      });

      const res = await checkSingleAnimeUpdate(item);
      const mainIdx = updatedList.findIndex((a) => a.id === item.id);
      if (mainIdx >= 0) {
        updatedList[mainIdx] = res.updatedAnime;
      }

      // The LINE alert for a new episode goes out from the new-episode watcher below
      if (res.updated) updatedCount++;
    }

    updateAnimeState(updatedList);
    setIsCheckingUpdates(false);
    setCheckProgress(null);

    if (updatedCount > 0) {
      showToast(
        `🎉 พบตอนใหม่ ${updatedCount} เรื่อง! อัปเดตข้อมูลล่าสุดเรียบร้อยแล้ว`,
        'success'
      );
    } else {
      showToast('เช็คครบทุกเรื่องแล้ว ตอนปัจจุบันเป็นตอนล่าสุดทั้งหมด ✓', 'info');
    }
  }, [animeList, isCheckingUpdates, lineConfig, showToast, updateAnimeState]);

  // Auto-check interval timer
  const checkUpdatesRef = useRef(handleCheckUpdates);
  checkUpdatesRef.current = handleCheckUpdates;

  useEffect(() => {
    if (!lineConfig.autoCheckMinutes || lineConfig.autoCheckMinutes <= 0) return;
    const ms = lineConfig.autoCheckMinutes * 60 * 1000;
    const timer = setInterval(() => {
      checkUpdatesRef.current();
    }, ms);
    return () => clearInterval(timer);
  }, [lineConfig.autoCheckMinutes]);

  // Send single anime notification to LINE
  const handleSendSingleLineNotify = async (anime: Anime) => {
    if (!lineConfig.channelAccessToken && !lineConfig.webhookUrl) {
      setIsLineModalOpen(true);
      showToast(
        'กรุณาใส่ LINE Channel Access Token หรือ Webhook URL ก่อนส่งแจ้งเตือนครับ',
        'info'
      );
      return;
    }
    setIsSendingLine(true);
    const res = await sendLineNotification(lineConfig, {
      anime,
      badge: `🔔 แจ้งเตือนอนิเมะ • ตอนที่ ${anime.latestEp}`,
    });
    setIsSendingLine(false);
    if (res.success) {
      showToast(`ส่งแจ้งเตือนเรื่อง "${anime.title}" เข้า LINE สำเร็จ! 🎉`, 'success');
    } else {
      showToast(`ส่ง LINE ไม่สำเร็จ: ${res.error}`, 'error');
    }
  };

  // Send Today's Airing Schedule to LINE
  const handleNotifyTodayToLine = async () => {
    if (!lineConfig.channelAccessToken && !lineConfig.webhookUrl) {
      setIsLineModalOpen(true);
      showToast(
        'กรุณาตั้งค่า LINE Channel Access Token ก่อนส่งคิวฉายวันนี้เข้า LINE ครับ',
        'info'
      );
      return;
    }

    const todayId = getTodayBroadcastDay();
    const todayName =
      BROADCAST_DAYS.find((d) => d.id === todayId)?.nameTh || 'วันนี้';
    const todayItems = animeList.filter(
      (a) => a.broadcastDay === todayId && a.status !== 'จบแล้ว'
    );

    if (todayItems.length === 0) {
      showToast(`ไม่มีอนิเมะที่มีคิวออกอากาศใน${todayName}`, 'info');
      return;
    }

    setIsSendingLine(true);
    const lines = todayItems.map(
      (a, idx) =>
        `${idx + 1}. ${a.title}${a.thaiTitle ? ` (${a.thaiTitle})` : ''}\n   ${
          isOnBreak(a) ? `⏸️ งดฉายสัปดาห์นี้ (${a.onBreak!.label})` : `⏰ ${a.broadcastTime || 'รออัปเดต'}`
        } | ตอนล่าสุด: ${a.latestEp} | 📺 ${a.sources?.[0]?.name || 'Anime-Waku'}`
    );
    const summaryMsg = `📅 ตารางอนิเมะออกอากาศ${todayName} (${todayItems.length} เรื่อง)\n━━━━━━━━━━━━━━\n${lines.join(
      '\n\n'
    )}`;

    const res = await sendLineNotification(lineConfig, {
      textMessage: summaryMsg,
      anime: todayItems[0],
      badge: `📅 คิวอนิเมะฉาย${todayName} (${todayItems.length} เรื่อง)`,
    });
    setIsSendingLine(false);

    if (res.success) {
      showToast(`ส่งสรุปตารางฉาย${todayName}เข้า LINE สำเร็จแล้ว! 🎉`, 'success');
    } else {
      showToast(`ส่ง LINE ไม่สำเร็จ: ${res.error}`, 'error');
    }
  };

  const airingTodayCount = animeList.filter((a) => isAiringToday(a)).length;
  const lineNotifyCount = animeList.filter((a) => a.notifyLine).length;

  return (
    <div className="min-h-screen text-slate-100 flex flex-col relative pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
      {/* Sticky Top Header */}
      <Header
        activeView={activeView}
        onChangeView={setActiveView}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSearchBar={showSearchBar}
        onToggleSearchBar={() => setShowSearchBar((v) => !v)}
        isSortMode={isSortMode}
        onToggleSortMode={() => setIsSortMode((v) => !v)}
        onOpenAddModal={() => {
          setEditingAnime(null);
          setIsAnimeModalOpen(true);
        }}
        onOpenLineModal={() => setIsLineModalOpen(true)}
        onCheckUpdates={handleCheckUpdates}
        isCheckingUpdates={isCheckingUpdates}
        airingTodayCount={airingTodayCount}
        lineNotifyCount={lineNotifyCount}
        onExportBackup={() => exportAnimeBackupJSON(animeList)}
        onOpenLibrary={() => setIsLibraryOpen(true)}
      />

      {/* Live Progress Banner while checking episodes */}
      {isCheckingUpdates && checkProgress && (
        <div className="sticky top-16 sm:top-[4.5rem] z-30 max-w-2xl mx-auto mt-2 px-3 w-full">
          <div className="bg-[#0d1020]/90 backdrop-blur-xl border border-teal-400/30 rounded-2xl p-3 shadow-xl shadow-black/40 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Loader2 className="w-5 h-5 text-teal-400 animate-spin shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-bold text-white truncate">
                  กำลังเช็คตอนใหม่ ({checkProgress.current}/{checkProgress.total}):{' '}
                  <span className="text-teal-300">{checkProgress.title}</span>
                </div>
                <div className="text-[11px] text-slate-400">
                  สแกนจาก Anime-Waku.com และ YouTube • พบตอนใหม่แล้ว{' '}
                  {checkProgress.updatedCount} เรื่อง
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:bottom-6 inset-x-3 sm:inset-x-auto sm:right-5 z-50 sm:max-w-sm">
          <div
            className={`px-4 py-3 rounded-2xl border backdrop-blur-xl shadow-2xl shadow-black/50 flex items-center gap-2.5 text-xs sm:text-sm font-medium ${
              toast.type === 'success'
                ? 'bg-emerald-950/90 border-emerald-400/30 text-emerald-50'
                : toast.type === 'error'
                ? 'bg-rose-950/90 border-rose-400/30 text-rose-50'
                : 'bg-[#141029]/90 border-fuchsia-400/30 text-fuchsia-50'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : toast.type === 'error' ? (
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            ) : (
              <Sparkles className="w-4 h-4 text-purple-400 shrink-0" />
            )}
            <span>{toast.text}</span>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="relative z-10 flex-1">
        {activeView !== 'explorer' && (
          <SeasonHeroBar
            selectedSeason={selectedSeason}
            onSelectSeason={setSelectedSeason}
            selectedYear={selectedYear}
            airingFilter={airingFilter}
            onSelectAiringFilter={setAiringFilter}
            onSelectYear={setSelectedYear}
            selectedAudio={selectedAudio}
            onSelectAudio={setSelectedAudio}
            animeList={animeList}
            filteredList={filteredAnimeList}
            countWith={countWithFilters}
            onResetFilters={resetFilters}
            onSwitchToSchedule={() => setActiveView('schedule')}
          />
        )}

        <div className={activeView === 'tier' ? 'block' : 'hidden'}>
          <TierBoard
            animeList={filteredAnimeList}
            isSortMode={isSortMode}
            onQuickPlus={handleQuickPlus}
            onQuickMinus={handleQuickMinus}
            onToggleNotify={handleToggleNotify}
            onEdit={(anime) => {
              setEditingAnime(anime);
              setIsAnimeModalOpen(true);
            }}
            onOpenDetail={(anime) => setDetailAnime(anime)}
            onReorder={handleReorder}
            onMoveUp={(id) => handleMoveUpDown(id, 'up')}
            onMoveDown={(id) => handleMoveUpDown(id, 'down')}
            onMoveToTier={handleMoveToTier}
            onStartSortMode={() => setIsSortMode(true)}
          />
        </div>

        <div className={activeView === 'schedule' ? 'block' : 'hidden'}>
          <ScheduleBoard
            animeList={filteredAnimeList}
            onQuickPlus={handleQuickPlus}
            onQuickMinus={handleQuickMinus}
            onToggleNotify={handleToggleNotify}
            onChangeBroadcastDay={handleChangeBroadcastDay}
            onEdit={(anime) => {
              setEditingAnime(anime);
              setIsAnimeModalOpen(true);
            }}
            onOpenDetail={(anime) => setDetailAnime(anime)}
            onCheckUpdates={handleCheckUpdates}
            isCheckingUpdates={isCheckingUpdates}
            onNotifyTodayToLine={handleNotifyTodayToLine}
            isSendingLine={isSendingLine}
            onMergeDuplicates={handleMergeDuplicates}
          />
        </div>

        <div className={activeView === 'explorer' ? 'block' : 'hidden'}>
          <WakuExplorerView
            existingAnimeList={animeList}
            onQuickAddFromWaku={handleQuickAddFromWaku}
            onRemoveFromWaku={handleQuickRemoveFromWaku}
          />
        </div>
      </div>

      {/* Modals */}
      <AnimeModal
        isOpen={isAnimeModalOpen}
        onClose={() => {
          setIsAnimeModalOpen(false);
          setEditingAnime(null);
        }}
        onSave={handleSaveAnime}
        onDelete={handleDeleteAnime}
        initialData={editingAnime}
      />

      <AnimeDetailModal
        anime={detailAnime}
        onClose={() => setDetailAnime(null)}
        onEdit={(anime) => {
          setEditingAnime(anime);
          setIsAnimeModalOpen(true);
        }}
        onUpdateAnime={(updated) => {
          handleSaveAnime(updated);
          setDetailAnime(updated);
        }}
        onSendSingleLineNotify={handleSendSingleLineNotify}
        isSendingLine={isSendingLine}
      />

      <OfflineLibraryModal
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onWatched={(ep) => {
          const target = animeList.find((x) => x.id === ep.animeId);
          if (target && ep.number > target.watchedEp) handleSaveAnime({ ...target, watchedEp: ep.number });
        }}
      />

      <LineNotifyModal
        isOpen={isLineModalOpen}
        onClose={() => setIsLineModalOpen(false)}
        config={lineConfig}
        onSaveConfig={(cfg) => applyLineConfig(cfg, true)}
        sampleAnime={animeList[0]}
        onNotifyTodaySchedule={handleNotifyTodayToLine}
      />
    </div>
  );
};
